'use client';
import React from 'react';
import { X, Ship, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { InspectionRecord } from './mockInspections';

interface Props {
  record: InspectionRecord;
  onClose: () => void;
}

const LSD_LABEL: Record<number, string> = { 1: 'Normal', 2: 'Minor', 3: 'Moderate', 4: 'Severe', 5: 'Critical' };

// Mock category breakdown for the drawer
const CATEGORY_BREAKDOWN = [
  { code: 'CC', label: 'Classification', lsd: 2, findings: 0 },
  { code: 'HS', label: 'Hull & Structure', lsd: 4, findings: 1 },
  { code: 'MD', label: 'Mooring Decks', lsd: 8, findings: 3 },
  { code: 'WD', label: 'Weather Deck', lsd: 3, findings: 1 },
  { code: 'BT', label: 'Ballast Systems', lsd: 2, findings: 0 },
  { code: 'AC', label: 'Accommodation', lsd: 3, findings: 1 },
  { code: 'BN', label: 'Bridge & Nav', lsd: 4, findings: 1 },
  { code: 'ER', label: 'Engine Room', lsd: 6, findings: 2 },
  { code: 'FF', label: 'Fire Fighting', lsd: 2, findings: 0 },
  { code: 'LS', label: 'Life Saving', lsd: 3, findings: 1 },
  { code: 'SW', label: 'Safety Mgmt', lsd: 4, findings: 1 },
  { code: 'PC', label: 'Pollution Ctrl', lsd: 2, findings: 0 },
  { code: 'OM', label: 'Onboard Mgmt', lsd: 3, findings: 1 },
  { code: 'VC', label: 'Cargo Systems', lsd: 1, findings: 0 },
];

export default function InspectionDetailDrawer({ record, onClose }: Props) {
  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl z-50 bg-white shadow-modal overflow-y-auto animate-slide-in-right scrollbar-thin"
        style={{ borderLeft: '1px solid hsl(var(--border))' }}>
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b flex items-start justify-between gap-4"
          style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'hsl(218 70% 22% / 0.1)' }}>
              <Ship size={18} style={{ color: 'hsl(var(--primary))' }} />
            </div>
            <div>
              <h2 className="font-bold text-base">{record.vesselName}</h2>
              <p className="font-mono text-xs text-muted">IMO {record.imoNumber}</p>
            </div>
          </div>
          <button className="p-2 rounded-lg hover:bg-muted transition-colors flex-shrink-0" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* LSD Score Hero */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1 rounded-xl p-4 text-center"
              style={{
                background: record.totalLsdScore > 60 ? 'hsl(355 75% 58% / 0.08)' :
                  record.totalLsdScore > 40 ? 'hsl(38 92% 50% / 0.08)' : 'hsl(145 60% 40% / 0.08)',
              }}>
              <p className="text-3xl font-bold tabular-nums font-mono"
                style={{
                  color: record.totalLsdScore > 60 ? 'hsl(355 75% 38%)' :
                    record.totalLsdScore > 40 ? 'hsl(38 72% 32%)' : 'hsl(145 60% 28%)',
                }}>
                {record.totalLsdScore}
              </p>
              <p className="text-xs text-muted mt-1">Total LSD</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ background: 'hsl(355 75% 58% / 0.08)' }}>
              <p className="text-3xl font-bold tabular-nums font-mono" style={{ color: 'hsl(355 75% 38%)' }}>
                {record.criticalFindings}
              </p>
              <p className="text-xs text-muted mt-1">Critical</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ background: 'hsl(38 92% 50% / 0.08)' }}>
              <p className="text-3xl font-bold tabular-nums font-mono" style={{ color: 'hsl(38 72% 32%)' }}>
                {record.severeFindings}
              </p>
              <p className="text-xs text-muted mt-1">Severe</p>
            </div>
          </div>

          {/* Vessel & Inspection Info */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted mb-3">Vessel Details</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Vessel Type', value: record.vesselType },
                { label: 'Flag', value: record.flag },
                { label: 'Deadweight', value: `${record.deadweight.toLocaleString()} MT` },
                { label: 'Classification', value: record.classificationSociety },
                { label: 'Ship Owner', value: record.owner },
                { label: 'Ship Manager', value: record.manager },
              ].map(({ label, value }) => (
                <div key={`drawer-${label}`} className="rounded-lg p-3" style={{ background: 'hsl(var(--secondary))' }}>
                  <p className="text-xs text-muted mb-0.5">{label}</p>
                  <p className="text-xs font-semibold">{value}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted mb-3">Inspection Details</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Inspector', value: record.inspector },
                { label: 'Organisation', value: record.inspectorOrg },
                { label: 'Date', value: record.dateOfInspection },
                { label: 'Type', value: record.inspectionType },
                { label: 'Location', value: `${record.port}, ${record.country}` },
                { label: 'Customer Scope', value: record.customerScope },
                { label: 'Completion', value: `${record.completionPct}% (${record.itemsAnswered}/${record.totalItems})` },
                { label: 'PSC Deficiencies', value: record.pscDeficiencies.toString() },
              ].map(({ label, value }) => (
                <div key={`drawer-insp-${label}`} className="rounded-lg p-3" style={{ background: 'hsl(var(--secondary))' }}>
                  <p className="text-xs text-muted mb-0.5">{label}</p>
                  <p className="text-xs font-semibold">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* PSC Detention Alert */}
          {record.pscDetention && (
            <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'hsl(355 75% 58% / 0.08)', border: '1px solid hsl(355 75% 58% / 0.3)' }}>
              <AlertTriangle size={16} style={{ color: 'hsl(var(--destructive))', flexShrink: 0, marginTop: 1 }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'hsl(var(--destructive))' }}>PSC Detention Recorded</p>
                <p className="text-xs text-muted mt-0.5">{record.pscDeficiencies} deficiencies noted during last PSC inspection. Corrective actions required.</p>
              </div>
            </div>
          )}

          {/* Category LSD Breakdown */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted mb-3">LSD Score by Category</h3>
            <div className="space-y-2">
              {CATEGORY_BREAKDOWN.map((cat) => (
                <div key={`cat-breakdown-${cat.code}`} className="flex items-center gap-3">
                  <span className="font-mono text-xs font-semibold w-7 flex-shrink-0 text-primary">{cat.code}</span>
                  <span className="text-xs flex-1 truncate text-muted">{cat.label}</span>
                  <div className="flex items-center gap-2 w-36">
                    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(cat.lsd / 10) * 100}%`,
                          background: cat.lsd >= 7 ? 'hsl(var(--destructive))' : cat.lsd >= 4 ? 'hsl(var(--warning))' : 'hsl(var(--success))',
                        }}
                      />
                    </div>
                    <span className="font-mono text-xs font-semibold tabular-nums w-5 text-right">{cat.lsd}</span>
                  </div>
                  {cat.findings > 0 ? (
                    <span className="badge badge-critical text-xs w-12 justify-center">{cat.findings} def</span>
                  ) : (
                    <CheckCircle2 size={13} style={{ color: 'hsl(var(--success))', width: 48 }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}